import React, { useState, useEffect } from "react";
import { Modal, Form, Button, Row, Col } from "react-bootstrap";
import CustomSelect from "../general/CustomSelect";
import { ModalHandler } from "../general/ModalHandler";
import { fetchAllShippingCosts, updateShippingCost, updateAllShippingCosts } from "../../api/auth";
import { IShippingCost } from "../../interfaces/IShippingCost";
import { IConfirmModal } from "../../interfaces/IModal";
import "./ShippingCostsModal.scss";
import {
  faCheckCircle,
  faExclamationTriangle,
} from "@fortawesome/free-solid-svg-icons";

interface ShippingCostsModalProps {
  show: boolean;
  onHide: () => void;
  axiosInstance: any;
}

interface OptionType {
  value: string;
  label: string;
}

const ShippingCostsModal: React.FC<ShippingCostsModalProps> = ({
  show,
  onHide,
  axiosInstance,
}) => {
  const [selectedState, setSelectedState] = useState<OptionType | null>(null);
  const [shippingCost, setShippingCost] = useState<string>("");
  const [deliveryDays, setDeliveryDays] = useState<string>("");
  const [isActive, setIsActive] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [shippingCosts, setShippingCosts] = useState<IShippingCost[]>([]);
  const [editingShippingCost, setEditingShippingCost] =
    useState<IShippingCost | null>(null);
  const [stateOptions, setStateOptions] = useState<OptionType[]>([]);
  const [modal, setModal] = useState<IConfirmModal | null>(null);
  
  // Estados para actualizar todos los costos
  const [globalCost, setGlobalCost] = useState<string>("");
  const [globalDeliveryDays, setGlobalDeliveryDays] = useState<string>("");
  const [isUpdatingAll, setIsUpdatingAll] = useState<boolean>(false);
  const [showBulkEdit, setShowBulkEdit] = useState<boolean>(false);

  /**
   * Effect hook that loads shipping costs when the modal opens
   * Executes every time the 'show' value changes
   */
  useEffect(() => {
    if (show) {
      loadShippingCosts();
    }
  }, [show]);

  /**
   * Loads all shipping costs from the API and generates state selector options
   * @async
   * @function loadShippingCosts
   * @returns {Promise<void>} Promise that resolves when data is loaded
   * @throws {Error} Error if API data loading fails
   */
  const loadShippingCosts = async () => {
    try {
      const costs = await fetchAllShippingCosts(axiosInstance);
      setShippingCosts(costs);

      const options = costs.map((cost) => ({
        value: cost.state,
        label: cost.state,
      }));

      // Sort alphabetically
      options.sort((a, b) => a.label.localeCompare(b.label));

      setStateOptions(options);
    } catch (error) {
      console.error("Error loading shipping costs:", error);
    }
  };

  /**
   * Handles state selection change in the selector
   * Automatically loads existing data for the selected state or clears fields
   * @function handleStateChange
   * @param {OptionType | null} newValue - The new selected state or null if deselected
   * @returns {void}
   */
  const handleStateChange = (newValue: OptionType | null) => {
    setSelectedState(newValue);

    if (newValue) {
      // Find the cost for this state
      const existingCost = shippingCosts.find(
        (cost) => cost.state === newValue.value
      );
      if (existingCost) {
        setEditingShippingCost(existingCost);
        setShippingCost(existingCost.cost.toString());
        setDeliveryDays(existingCost.estimated_days.toString());
        setIsActive(existingCost.status === 1 ? true : false);
      }
    } else {
      setEditingShippingCost(null);
      setShippingCost("");
      setDeliveryDays("");
      setIsActive(true);
    }
  };

  /**
   * Shows a success modal after successfully updating a shipping cost
   * When closed, it also closes the main modal
   * @function showSuccessModal
   * @returns {void}
   */
  const showSuccessModal = () => {
    setModal({
      show: true,
      title: "Éxito",
      message: "Costo de envío actualizado exitosamente",
      icon: faCheckCircle,
      iconColor: "text-success",
      onHide: () => {
        setModal(null);
        handleClose();
      },
    });
  };

  /**
   * Shows an error modal with a custom message
   * @function showErrorModal
   * @param {string} message - The error message to display
   * @returns {void}
   */
  const showErrorModal = (message: string) => {
    setModal({
      show: true,
      title: "Error",
      message: message,
      icon: faExclamationTriangle,
      iconColor: "text-danger",
      onHide: () => setModal(null),
    });
  };

  /**
   * Shows a validation modal when required fields are missing
   * @function showValidationModal
   * @returns {void}
   */
  const showValidationModal = () => {
    setModal({
      show: true,
      title: "Campos requeridos",
      message: "Por favor, completa todos los campos",
      icon: faExclamationTriangle,
      iconColor: "text-warning",
      onHide: () => setModal(null),
    });
  };

  /**
   * Saves/updates the shipping cost for the selected state
   * Validates fields, updates data in the API and shows the result
   * @async
   * @function handleSave
   * @returns {Promise<void>} Promise that resolves when the operation completes
   * @throws {Error} Error if API update fails
   */
  const handleSave = async () => {
    if (!selectedState || !shippingCost || !deliveryDays) {
      showValidationModal();
      return;
    }

    if (!editingShippingCost) {
      showErrorModal(
        "Error: No se pudo encontrar el costo de envío para actualizar"
      );
      return;
    }

    setIsLoading(true);
    try {
      const updateData = {
        state: selectedState.value,
        cost: parseFloat(shippingCost),
        estimated_days: parseInt(deliveryDays),
        status: isActive,
      };

      await updateShippingCost(
        axiosInstance,
        editingShippingCost.id,
        updateData
      );

      await loadShippingCosts();
      showSuccessModal();
    } catch (error) {
      console.error("Error updating shipping cost:", error);
      showErrorModal("Error al actualizar el costo de envío");
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Closes the modal and resets all states to their initial values
   * @function handleClose
   * @returns {void}
   */
  const handleClose = () => {
    setSelectedState(null);
    setShippingCost("");
    setDeliveryDays("");
    setIsActive(true);
    setEditingShippingCost(null);
    setGlobalCost("");
    setGlobalDeliveryDays("");
    setShowBulkEdit(false);
    onHide();
  };

  /**
   * Updates all shipping costs with the same cost and estimated days
   * @async
   * @function handleUpdateAll
   * @returns {Promise<void>} Promise that resolves when the operation completes
   * @throws {Error} Error if API update fails
   */
  const handleUpdateAll = async () => {
    if (!globalCost || !globalDeliveryDays) {
      showValidationModal();
      return;
    }

    setIsUpdatingAll(true);
    try {
      const updateData = {
        cost: parseFloat(globalCost),
        estimated_days: parseInt(globalDeliveryDays),
      };

      await updateAllShippingCosts(axiosInstance, updateData);
      await loadShippingCosts();
      
      setModal({
        show: true,
        title: "Éxito",
        message: "Todos los costos de envío han sido actualizados exitosamente",
        icon: faCheckCircle,
        iconColor: "text-success",
        onHide: () => {
          setModal(null);
          setGlobalCost("");
          setGlobalDeliveryDays("");
        },
      });
    } catch (error) {
      console.error("Error updating all shipping costs:", error);
      showErrorModal("Error al actualizar todos los costos de envío");
    } finally {
      setIsUpdatingAll(false);
    }
  };

  return (
    <>
      <Modal show={show && !modal} onHide={handleClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Costo de envíos</Modal.Title>
        </Modal.Header>
        <Modal.Body className="shipping-costs-modal-body">
          <Form>
            {/* Botón para activar edición masiva */}
            {!showBulkEdit && (
              <div className="mb-4 text-center">
                <Button
                  variant="primary"
                  onClick={() => setShowBulkEdit(true)}
                  className="px-4"
                >
                  📦 Editar todos los estados
                </Button>
                <p className="text-muted small mt-2">
                  Establece el mismo costo y tiempo de entrega para todos los estados
                </p>
              </div>
            )}

            {/* Sección para actualizar todos los costos */}
            {showBulkEdit && (
              <div className="mb-4 p-3" style={{ 
                backgroundColor: "#f8f9fa", 
                border: "1px solid #dee2e6", 
                borderRadius: "8px" 
              }}>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="mb-0" style={{ color: "#495057", fontWeight: "600" }}>
                    📦 Editar todos los estados
                  </h6>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => setShowBulkEdit(false)}
                  >
                    ✕
                  </Button>
                </div>
                <p className="text-muted small mb-3">
                  Establece el mismo costo y tiempo de entrega para todos los estados de la república.
                </p>
                
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="small fw-bold">Costo de envío:</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="0.00"
                        value={globalCost}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^0-9.]/g, "");
                          setGlobalCost(value);
                        }}
                        className="no-spinners"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="small fw-bold">Tiempo estimado (días):</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Ej: 5"
                        value={globalDeliveryDays}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^0-9]/g, "");
                          setGlobalDeliveryDays(value);
                        }}
                        className="no-spinners"
                      />
                    </Form.Group>
                  </Col>
                </Row>
                
                <Button
                  variant="primary"
                  onClick={handleUpdateAll}
                  disabled={isUpdatingAll || !globalCost || !globalDeliveryDays}
                  className="w-100"
                >
                  {isUpdatingAll ? "Actualizando..." : "Actualizar todos los estados"}
                </Button>
              </div>
            )}

            {showBulkEdit && <hr className="my-4" />}

            <Form.Group className="mb-3">
              <Form.Label>Estado de la República</Form.Label>
              <CustomSelect
                options={stateOptions}
                value={selectedState}
                onChange={handleStateChange}
                placeholder="Selecciona un estado..."
                isFilteredIconVisible={false}
              />
            </Form.Group>

            {selectedState && (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>Costo de envío:</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="0.00"
                    value={shippingCost}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9.]/g, "");
                      setShippingCost(value);
                    }}
                    className="no-spinners"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Tiempo estimado de entrega (días):</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Ej: 5"
                    value={deliveryDays}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9]/g, "");
                      setDeliveryDays(value);
                    }}
                    className="no-spinners"
                  />
                </Form.Group>

                <Form.Group className="mb-3 d-flex align-items-center">
                  <Form.Label className="me-3 mb-0">Estatus:</Form.Label>
                  <Form.Check
                    type="switch"
                    id="status-switch"
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                    className="shipping-status-switch"
                  />
                  <span className="ms-2">
                    {isActive ? "Activo" : "Inactivo"}
                  </span>
                </Form.Group>
              </>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer className="justify-content-end">
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={isLoading || !editingShippingCost}
          >
            {isLoading ? "Actualizando..." : "Actualizar"}
          </Button>
        </Modal.Footer>
      </Modal>

      {modal && <ModalHandler modal={modal} />}
    </>
  );
};

export default ShippingCostsModal;
